import {ProjectMinRes} from '../tracker/project-tracker';
import {Type} from 'class-transformer';
import {
    ClassDisplayName,
    ColumnType,
    DetailGridIgnore,
    DetailGridProperties,
    DisplayName,
    FileUploadRes,
    RequestOverride,
    TableColumn,
    TableDisplayFn,
    TableIgnore,
    TableIndex,
    yascLevelOfDetail
} from '@yasc/yasc-material-components';
import {ContactPersonDetailRes} from '../user.contract';
import {formatFromMinutes, minToHours} from './general-task-utils';
import {TaskEffortRes, TaskEffortUpdateReq} from './task-effort';

class baseTask {
    @DisplayName('general_name')
    @TableIndex(0)
    @yascLevelOfDetail(0)
    @DetailGridProperties({row: 0, width: 3})
    @yascLevelOfDetail(1)
    name!: string;
    taskID?: string;

    @DisplayName('crm_contract_category_label')
    @TableIndex(2)
    @yascLevelOfDetail(2)
    category!: string;
    mockups: FileUploadRes[] = []
    @DisplayName('crm_contract_taskRisks_label')
    @TableColumn(ColumnType.Tag)
    @TableDisplayFn((val: string[]) => val.join(', '))
    @yascLevelOfDetail(1)
    risks: string[] = []
    @DisplayName('crm_contract_taskClarifications_label')
    @TableColumn(ColumnType.Tag)
    @TableDisplayFn((val: string[]) => val.join(', '))
    @yascLevelOfDetail(1)
    clarifications: string[] = []
    @DisplayName('crm_contract_taskBudget_label')
    @TableColumn(ColumnType.Number)
    @yascLevelOfDetail(1)
    maxBudget!: number
    currentPlanned!: number;
    @DisplayName('general_description')
    @yascLevelOfDetail(2)
    description?: string;
    recentAppointmentsCnt!: number;

    constructor(value?: Partial<baseTask>) {
        Object.assign(this, value)
    }
}

@ClassDisplayName('crm_contract_baseTaskClass_title')
export class BaseTaskRes {
    id!: string;
    taskId!: string;
    category!: string;
    mockup: string[] = [];
    contact: ContactPersonDetailRes[] = []
    references: BaseTaskRes[] = [];
    risks:string[] = [];
    clarifications:string[] = [];
    originalStart!: string;
    originalEnd!: string;
    originalEstimated!: number;
    @DisplayName('crm_task_currentStart_label')
    currentStart!: Date;
    @DisplayName('crm_task_currentEnd_label')
    currentEnd!: Date;
    @DisplayName('crm_task_currentEst_label')
    currentEstimated!: number;
    @DisplayName('crm_task_currentPlanned_label')
    currentPlanned!: number;
    taskType!: string;
    name?: string;
    description?: string;
    recentAppointmentsCnt!: number;

    @Type(() => ProjectMinRes)
    @DisplayName('general_project')
    @RequestOverride('projectId')
    @TableDisplayFn((val: ProjectMinRes) => val === undefined ? 'No Project Assigned' : val.name)
    @yascLevelOfDetail(3)
    project?: ProjectMinRes;

    @Type(() => ContactPersonDetailRes)
    @DisplayName('crm_contract_connectedContacts_label')
    @TableDisplayFn((val: ContactPersonDetailRes[]) => val.map(x => x.firstName.charAt(0) + '. ' + x.lastName).join(', '))
    @yascLevelOfDetail(2)
    contacts: ContactPersonDetailRes[] = []

    @DisplayName('crm_contract_taskCurrentStart_label')
    @DetailGridProperties({row: 3, width: 2})
    @TableColumn(ColumnType.Date)
    @yascLevelOfDetail(2)
    get currentStartDate(): Date {
        return new Date(this.currentStart);
    }

    set currentStartDate(date: Date) {
        this.currentEnd = date
    }

    @DisplayName('crm_contract_taskCurrentEnd_label')
    @DetailGridProperties({row: 3, width: 2})
    @TableColumn(ColumnType.Date)
    @yascLevelOfDetail(2)
    get currentEndDate(): Date {
        return new Date(this.currentEnd)
    }

    set currentEndDate(date: Date) {
        this.currentEnd = date
    }


    @DisplayName('crm_contract_referencedTasks_label')
    @TableIgnore(true)
    @DetailGridProperties({row: 5, width: 3})
    @TableColumn(ColumnType.Select)
    @RequestOverride('referencedTaskIds')
    @Type(() => BaseTaskRes)
    @yascLevelOfDetail(2)
    referencedTasks: BaseTaskRes[] = []

    @DisplayName('crm_contract_referencedByTasks_label')
    @TableIgnore(true)
    @DetailGridIgnore()
    @TableColumn(ColumnType.Select)
    @RequestOverride('referencedByTaskIds')
    @Type(() => BaseTaskRes)
    @yascLevelOfDetail(2)
    referencedByTask: BaseTaskRes[] = []

    @DisplayName('crm_contract_taskEffort_label')
    @Type(() => TaskEffortRes)
    @TableDisplayFn((val: TaskEffortRes) => val.name)
    @yascLevelOfDetail(2)
    efforts: TaskEffortRes[] = []

    @DisplayName('crm_contract_customerFacingId_label')
    @TableIndex(1)
    @yascLevelOfDetail(1)
    @DetailGridIgnore()
    get customerFacingId(): string {
        if (this.taskId !== undefined) return this.taskId;
        return this.id.toString()
    }

    @DisplayName('crm_contract_taskEstimatedEffortSum_label')
    @DetailGridIgnore()
    @TableDisplayFn((val) => val.formatted)
    @yascLevelOfDetail(2)
    get estimatedEffortSum() {
        let effort_in_min = this.efforts.reduce((a, b) => a + (b.estimatedDurationInMin ?? 0), 0)
        let formatted = formatFromMinutes(effort_in_min)
        return {formatted: formatted, hours: minToHours(effort_in_min), rawMinutes: effort_in_min}
    }

    @DisplayName('crm_contract_taskActualEffortSum_label')
    @TableDisplayFn((val) => val.formatted)
    @DetailGridIgnore()
    @yascLevelOfDetail(2)
    get actualEffortSum() {
        let effort_in_min = this.efforts.reduce((a, b) => a + (b.actualDurationInMin ?? 0), 0)
        let formatted = formatFromMinutes(effort_in_min)
        return {formatted: formatted, hours: minToHours(effort_in_min), rawMinutes: effort_in_min}
    }

    private toUpdateCreate(value: Partial<BaseTaskRes>): BaseTaskCreateReq {
        let create = new BaseTaskCreateReq({})
        create.start = value.currentStart!
        create.end = value.currentEnd!
        create.estimated = value.currentEstimated ?? 0
        create.contactIds = value.contacts?.map(x => x.id) ?? []
        create.referencedTaskIds = value.referencedTasks?.map(x => x.id) ?? []
        create.referencedByTaskIds = value.referencedByTask?.map(x => x.id) ?? []
        create.projectId = value.project?.id
        return create
    }

    public toCreate(): BaseTaskCreateReq {
        return this.toUpdateCreate(this)
    }

    public toUpdate(): BaseTaskUpdateReq {
        let update = this.toUpdateCreate(this) as BaseTaskUpdateReq;
        // update.id = this.id!
        // update.efforts = this.efforts.map(x => new TaskEffortUpdateReq(x))
        return update
    }
}

export class BaseTaskCreateReq extends baseTask {
    projectId?: number
    contactIds: number[] = []
    referencedTaskIds: string[] = []
    referencedByTaskIds: string[] = []
    estimated!: number;
    start!: Date;
    end!: Date;

    constructor(value: Partial<BaseTaskCreateReq>) {
        super(value);
        Object.assign(this, value)
    }
}

export class BaseTaskUpdateReq extends BaseTaskCreateReq {
    id!: string;
    efforts: TaskEffortUpdateReq[] = []

    constructor(value: Partial<BaseTaskUpdateReq>) {
        super(value);
        Object.assign(this, value)
    }

    @DisplayName('crm_contract_taskEstimatedEffortSum_label')
    @DetailGridIgnore()
    @TableDisplayFn((val) => val.formatted)
    @yascLevelOfDetail(1)
    get estimatedEffortSum() {
        let effort_in_min = this.efforts.reduce((a, b) => a + (b.estimatedDurationInMin ?? 0), 0)
        let formatted = formatFromMinutes(effort_in_min)
        return {formatted: formatted, hours: minToHours(effort_in_min), rawMinutes: effort_in_min}
    }

    @DisplayName('crm_contract_taskActualEffortSum_label')
    @TableDisplayFn((val) => val.formatted)
    @DetailGridIgnore()
    @yascLevelOfDetail(1)
    get actualEffortSum() {
        let effort_in_min = this.efforts.reduce((a, b) => a + (b.actualDurationInMin ?? 0), 0)
        let formatted = formatFromMinutes(effort_in_min)
        return {formatted: formatted, hours: minToHours(effort_in_min), rawMinutes: effort_in_min}
    }
}
